import { useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpAnchor, Pill } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SignalCell } from '@/versions/v1/schema/spc';
import type { ShiftWindow } from '@/versions/v1/shared/clock.derive';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { GmCard } from './GmTabs';
import { stageLadder, type LadderCell, type StageId } from './StageLadder.transform';

const CELL_LABEL: Partial<Record<SignalCell, string>> = {
  drift: 'Drift',
  consequence: 'Consequence',
  'not-capable': 'Not capable',
};

const CELL_PILL_TONE: Partial<Record<SignalCell, 'warning' | 'danger'>> = {
  drift: 'warning',
  consequence: 'danger',
  'not-capable': 'warning',
};

/** The marker's colour: state colour for the abnormal reading only; an in-band reading stays ink. */
const MARKER_COLOR: Record<LadderCell['cell'], string> = {
  quiet: 'var(--ink-secondary)',
  drift: 'var(--state-warning)',
  'not-capable': 'var(--state-warning)',
  consequence: 'var(--state-blocked)',
  'not-computed': 'var(--ink-tertiary)',
  'no-series': 'var(--ink-tertiary)',
};

const BASIS_WINDOW_FALLBACK_PCT = 0.1;

/**
 * Where the reading sits in its comparison window, 0–100 (locked comp
 * `landing-control-met.html` `.meter`). MS-F7 still binds: the window is
 * scaled to what the reading is compared against, never to its own
 * magnitude. A basis centres the window (half-width = the reading's own
 * tolerance, else ±10 % of the basis); a band with no basis fills the middle
 * half of the window (adjudication 2 — the Zn float pH reads `cp-zn-ph`).
 */
function meterPosition(cell: LadderCell): { pct: number; band?: { left: number; right: number } } | null {
  if (cell.basis) {
    const half = cell.toleranceHalfWidth ?? Math.abs(cell.basis.value) * BASIS_WINDOW_FALLBACK_PCT;
    if (!half) return { pct: 50 };
    return { pct: 50 + ((cell.raw - cell.basis.value) / half) * 50 };
  }
  if (cell.band) {
    const width = cell.band.high - cell.band.low;
    if (width <= 0) return null;
    const origin = cell.band.low - width / 2;
    return { pct: ((cell.raw - origin) / (width * 2)) * 100, band: { left: 25, right: 25 } };
  }
  return null;
}

function DeviationMeter({ cell }: { cell: LadderCell }) {
  const position = meterPosition(cell);
  if (!position) {
    // MS-F7: nothing to scale a meter against; the honest state is no bar.
    return <span aria-hidden="true" className="block h-0 border-t border-dashed border-[var(--hairline-strong)]" />;
  }
  const pct = Math.min(97, Math.max(3, position.pct));
  const reference = cell.basis ? `${cell.basis.label} ${cell.basis.value.toLocaleString()}` : `band ${cell.bandLabel ?? ''}`;
  return (
    <span
      className="relative block h-1.5 rounded-full bg-[var(--hairline-strong)]"
      title={`${cell.value} ${cell.unit} vs ${reference}`}
      data-ladder-meter={cell.readingId}
    >
      {position.band ? (
        <span
          aria-hidden="true"
          className="absolute inset-y-0 rounded-full bg-[color-mix(in_srgb,var(--state-online)_14%,transparent)]"
          style={{ left: `${position.band.left}%`, right: `${position.band.right}%` }}
        />
      ) : (
        <span aria-hidden="true" className="absolute -inset-y-0.5 left-1/2 w-px bg-[var(--ink-tertiary)]" />
      )}
      <span
        aria-hidden="true"
        className="absolute -top-[3px] h-3 w-[3px] -translate-x-1/2 rounded-sm"
        style={{ left: `${pct}%`, backgroundColor: MARKER_COLOR[cell.cell] }}
      />
    </span>
  );
}

/** Dashed neutral provenance chip (comp `.prov`); the source rides in the tooltip. */
function SynChip({ text }: { text: string }) {
  return (
    <span
      title={text}
      className="inline-flex items-center whitespace-nowrap rounded-full border border-dashed border-[var(--hairline-strong)] px-1.5 text-[10.5px] leading-4 text-muted"
    >
      {text}
    </span>
  );
}

/** Case code chip (comp `.idc`): the open case a row's excursion already carries. */
function CaseCodeChip({ code }: { code: string }) {
  return (
    <span className="inline-flex items-center whitespace-nowrap rounded-md border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-1.5 text-[10.5px] font-medium leading-4 tracking-[0.02em] text-ink-secondary">
      {code}
    </span>
  );
}

function RowState({ cellState }: { cellState: LadderCell['cell'] }) {
  if (cellState === 'no-series' || cellState === 'not-computed') return <span />;
  if (cellState === 'quiet') return <span className="text-[11.5px] text-muted">in band</span>;
  const label = CELL_LABEL[cellState];
  const tone = CELL_PILL_TONE[cellState];
  if (!label || !tone) return <span />;
  return (
    <span>
      <Pill tone={tone}>{label}</Pill>
    </span>
  );
}

function DivergenceLine({ cell }: { cell: LadderCell }) {
  const divergence = cell.sampleDivergence;
  if (!divergence) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-[11.5px] text-muted">
      walking · in band
      {divergence.requested ? (
        <Link to={divergence.samplesHref}>
          <Pill tone="default">samples {divergence.requestedAt}</Pill>
        </Link>
      ) : null}
    </span>
  );
}

// The comp's seven columns at 1470. A long reading name wraps at every width
// (polish.P26 r1: the 1470 truncation clipped `Zn contamination of final Pb
// c…`, and no label may clip at any width).
const ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_68px_40px_74px_50px_36px_68px] items-center gap-1.5 min-[1440px]:grid-cols-[minmax(0,1fr)_76px_60px_76px_52px_38px_70px] min-[1440px]:gap-2';

function LadderRow({ cell }: { cell: LadderCell }) {
  const noRead = cell.cell === 'no-series' || cell.cell === 'not-computed';
  return (
    <li data-scroll-cue-row data-ladder-row={cell.readingId} title={cell.workbookTooltip} className="border-t border-[var(--hairline)]">
      <div className={cn(ROW_GRID, 'py-[5px] pl-3.5 text-[12.5px] tabular-nums')}>
        <Link to={cell.nodeHref} title={cell.label} className="leading-snug text-ink-primary hover:underline">
          {cell.label}
        </Link>
        {noRead ? (
          <span className="col-span-3 text-[11.5px] text-muted">
            {cell.cell === 'no-series' ? 'no series at this seed' : 'not computed'}
          </span>
        ) : (
          <>
            <span className="whitespace-nowrap text-right font-semibold text-ink-primary">
              {cell.value}
              {cell.unit ? <small className="ml-[3px] text-[11px] font-normal text-muted">{cell.unit}</small> : null}
            </span>
            <DeviationMeter cell={cell} />
            <span className="whitespace-nowrap text-[11.5px] text-muted">
              {cell.basis ? `${cell.basis.label} ${cell.basis.value.toLocaleString()}` : (cell.bandLabel ?? '')}
            </span>
          </>
        )}
        <RowState cellState={cell.cell} />
        <span className="text-[11.5px] text-muted">{cell.since ?? ''}</span>
        <span className="flex justify-end">{cell.caseLabel ? <CaseCodeChip code={cell.caseLabel} /> : null}</span>
      </div>
      {cell.sampleDivergence ? (
        <div className={cn(ROW_GRID, 'pb-[5px] pl-3.5')}>
          <span />
          <span />
          <span className="col-span-3">
            <DivergenceLine cell={cell} />
          </span>
        </div>
      ) : null}
    </li>
  );
}

function StageHeader({
  label,
  outcome,
  state,
  hidden,
  synCount,
  synTotal,
  since,
  nodeHref,
  expanded,
  rowsShown,
  onToggle,
}: {
  label: string;
  outcome: LadderCell;
  state: SignalCell | 'not-computed';
  hidden: number;
  synCount: number;
  synTotal: number;
  since?: string;
  nodeHref: string;
  expanded: boolean;
  rowsShown: boolean;
  onToggle: () => void;
}) {
  const chip = CELL_LABEL[state as SignalCell];
  const chipTone = CELL_PILL_TONE[state as SignalCell];
  const isQuietStage = state === 'quiet';
  // Rule of three (Q4): a quiet stage collapses to zero visible rows, so
  // `hidden` IS the full row count when collapsed; a non-quiet stage shows
  // its worst three first, `hidden` is the remainder. The button names
  // whichever is true; it disappears once nothing is left to disclose.
  const expandable = hidden > 0 || expanded;
  const buttonLabel = expanded ? 'Show fewer' : isQuietStage ? `${hidden} in band` : `${hidden} more`;
  const noRead = outcome.cell === 'no-series';
  return (
    <div className="flex min-h-[34px] flex-wrap items-center gap-x-2.5 gap-y-1 py-2 tabular-nums" data-ladder-stage-head>
      <Link to={nodeHref} className="whitespace-nowrap text-[13.5px] font-semibold text-ink-primary hover:underline">
        {label}
      </Link>
      <span className="whitespace-nowrap text-xs text-muted" data-ladder-stage-figure>
        {noRead ? (
          outcome.value
        ) : (
          <>
            <span className="text-[15px] font-semibold text-ink-primary">{outcome.value}</span>
            {outcome.unit ? ` ${outcome.unit}` : ''}
            {outcome.basis ? ` vs ${outcome.basis.label.toLowerCase()} ${outcome.basis.value.toLocaleString()}` : ''}
          </>
        )}
      </span>
      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
        {!isQuietStage && chip && chipTone ? <Pill tone={chipTone}>{chip}</Pill> : null}
        {since ? <span className="whitespace-nowrap text-xs text-muted">since {since}</span> : null}
        {!rowsShown ? <DivergenceLine cell={outcome} /> : null}
      </span>
      <span className="ml-auto flex items-center gap-2">
        {synCount > 0 ? <SynChip text={`[syn] ${synCount} of ${synTotal}`} /> : null}
        {expandable ? (
          <button
            type="button"
            onClick={onToggle}
            className="whitespace-nowrap text-xs font-semibold text-ink-secondary hover:text-ink-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-500)]"
          >
            {buttonLabel}
          </button>
        ) : null}
      </span>
    </div>
  );
}

/**
 * `Where the plant is off — by stage` (met-stage-ladder, polish.P26 #713 to the
 * locked comp): five plant-order stages, each header setting its outcome as a
 * figure against design (B's stage-header figure); a drifting stage shows its
 * worst three rows on first paint, a quiet stage collapses to `N in band`
 * (adjudication 1). Every cell is the SPC engine's, never authored.
 */
export function StageLadder({
  state,
  slug,
  window,
  compact = false,
  className,
}: {
  state: SessionState;
  slug: string;
  /** A roster period scopes the listed events; the landing sets none (adjudication 1). */
  window?: ShiftWindow;
  /** Header-only stages on first paint; rows behind disclosure. Default off. */
  compact?: boolean;
  className?: string;
}) {
  const [expanded, setExpanded] = useState<ReadonlySet<StageId>>(new Set());
  const advisory = useAdvisory();
  const model = stageLadder(state, slug, { expanded, window, compact }, advisory);
  if (model.stages.length === 0) return null;

  const toggle = (id: StageId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Commentable name="StageLadder">
      <GmCard
        title="Where the plant is off — by stage"
        meta={<HelpAnchor doc="overview/met-stage-ladder" />}
        className={className}
        data-stage-ladder
      >
        <div className="flex flex-col">
          {model.stages.map((stage) => (
            <div key={stage.id} className="border-b border-[var(--color-divider)] last:border-b-0" data-ladder-stage={stage.id}>
              <StageHeader
                label={stage.label}
                outcome={stage.outcome}
                state={stage.state}
                hidden={stage.hidden}
                synCount={stage.synCount}
                synTotal={stage.synTotal}
                since={stage.since}
                nodeHref={stage.nodeHref}
                expanded={expanded.has(stage.id)}
                rowsShown={stage.rows.some((row) => row.readingId === stage.outcome.readingId)}
                onToggle={() => toggle(stage.id)}
              />
              {stage.rows.length > 0 ? (
                <ul>
                  {stage.rows.map((cell) => (
                    <LadderRow key={cell.readingId} cell={cell} />
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>
      </GmCard>
    </Commentable>
  );
}
